import React, { Fragment } from "react"
import PropTypes from "prop-types"
import Switch from "react-switch"
import Col from "react-bootstrap/lib/Col"
import debounce from "lodash/debounce"
import StickyBox from "react-sticky-box"

import * as VenuesAPI from "../apis/VenuesAPI"
import { diff_in_days } from "../utils/dates/diff_in_days"
import { setToLocalStorage, getFromLocalStorage } from "../utils/localStorage"
import BrowserLocationHelper from "../utils/browser_location_helper"
import sendAddressToIntercom from "../utils/Intercom/sendAddressToIntercom"
import EmptyVenuesAlert from "./components/EmptyVenuesAlert/EmptyVenuesAlert"
import CampaignsBannerHorizontalList from "./components/CampaignsBanner/CampaignsBannerHorizontalList"
import VenueChooserSearchBar from "./components/filters/VenueChooserSearchBar"
import VenueCategoriesChooser from "./components/filters/VenueCategoriesChooser"
import VenueFeaturesChooser from "./components/filters/VenueFeaturesChooser"
import VenuesFilterModal from "./components/filters/VenuesFilterModal/index.jsx"
import LoadingVenues from "./components/LoadingVenues"
import ErrorMessage from "./components/ErrorMessage"
import ServicesList from "./ServicesList"
import VenuesList from "./VenuesList/VenuesList"
import ZoneNotCovered from "./ZoneNotCovered/"
import { ORDER_MODE } from "./OrderMode"

import {
  gtmSetUserProperties,
  logVenueClicked,
  logServiceClicked,
} from "../apis/Analytics/gtmActions"
import { GetSearchedServices } from "../apis/VenuesAPI"

import { getVisibleVenues } from "./filters"

const storejs = require("store")

import style from "./style.module.sass"
import CategoriesHorizontalList from "./components/CategoriesHorizontalList";
import SubCategoriesHorizontalList from "./components/SubCategoriesHorizontalList"
import SelectedCategoriesTitle from "./components/SelectedCategoriesTitle";
import { Carousel } from "react-bootstrap";
import VenuesCarousels from "./components/VenuesCarousels/VenuesCarousels";
import logVisitedVenueToIntercom from "../utils/Intercom/logVisitedVenueToIntercom";

const FILTERING_EXPIRATION = "filtering_expiration"
const FILTERING_CATEGORIES = "filtering_categories"
const FILTERING_FEATURES = "filtering_features"

function getVenueURL(url, citySlug, venueSlug) {
  return url.replace(":city", citySlug).replace(":restaurant", venueSlug)
}

function getInitialSelectedFeatures() {
  return {
    creditCard: false,
    discount: false,
    cash: false,
    new: false,
    promotion: false,
  }
}

function getInitialCategories(categories) {
  return categories
    .filter((category) => !category.is_root)
    .map((category) => {
      return { ...category, selected: false }
    })
}


function getRootCategories(categories) {
  return categories
    .filter((category) => category.is_root)
    .map((category, index) => {
      return { ...category, selected: index === 0 }
    })
}

export default class VenueChooserApp extends React.Component {
  constructor(props) {
    super(props)

    // check and reset stored filtering
    let now = new Date()
    let expiration = storejs.get(FILTERING_EXPIRATION)
    if (now > expiration) {
      storejs.set(FILTERING_CATEGORIES, null)
      storejs.set(FILTERING_FEATURES, null)
    }
    storejs.set(FILTERING_EXPIRATION, now.setMinutes(now.getMinutes() + 30))

    this.state = {
      errorMessage: null,
      categories: [],
      rootCategories: [],
      showFilterModal: false,
      resultsCount: 0,
      venues: [],
      isSearching: false,
      isLoading: false,
      searchFilteredVenues: [],
      searchFilteredServices: [],
      loaded: false,
      takeAwaySelected: false,
      selectedFeatures: {},
    }
  }

  componentDidMount() {
    this.loadVenues()
  }

  loadVenues = () => {
    let queryParam = { following_days: true }

    if (JSON.parse(getFromLocalStorage("take_away")) === true) {
      this.setState({
        takeAwaySelected: true,
      })
      queryParam = { take_away: true }
    }

    VenuesAPI.getVenues(this.props.location, queryParam)
      .then((data) => {
        if (data.delivery_zone) {
          gtmSetUserProperties({ delivery_zone: data.delivery_zone.name })

          sendAddressToIntercom(this.props.location, data.delivery_zone.name)
        }

        let categories = getInitialCategories(data.venue_categories)
        let storedCategories = storejs.get(FILTERING_CATEGORIES) || []
        categories.forEach((category) => {
          if (
            storedCategories.some((stored) => {
              return category.id === stored.id && stored.selected
            })
          ) {
            category.selected = true
          }
        })
        storejs.set(FILTERING_CATEGORIES, categories)

        let features = getInitialSelectedFeatures()
        let storedFeatures = storejs.get(FILTERING_FEATURES) || {}
        for (let key in features) {
          features[key] = features[key] || storedFeatures[key]
        }
        storejs.set(FILTERING_FEATURES, features)

        this.setState({
          loaded: true,
          zoneNotCovered: !data.delivery_zone && data.venues.length === 0,
          deliveryZone: data.delivery_zone,
          venues: data.venues,
          categories: categories,
          rootCategories: getRootCategories(data.venue_categories),
          selectedFeatures: features,
        })
      })
      .catch((err) => {
        // TODO: missing handling errors
        this.setState({
          errorMessage: I18n.t("pages.locations.venues.generic_loading_error"), // err.toString()
        })
      })
  }

  toggleTakeAwayModeWithLocalStorage = (takeawayMode) => {
    this.setState({ takeAwaySelected: takeawayMode })
    setToLocalStorage("take_away", takeawayMode)
  }

  handleOnAllCategoriesSelected = () => {
    let newCategories = getInitialCategories(this.state.categories)
    storejs.set(FILTERING_CATEGORIES, newCategories)

    this.setState({ categories: newCategories })
  }

  handleOnRootCategorySelected = (id) => {
    const newRootCategories = this.state.rootCategories.map(category => {
      return {
        ...category,
        selected: category.id === id
      }
    })

    // When the user changes the root selected category, deselect all the categories selected
    this.handleOnAllCategoriesSelected()

    this.setState({ rootCategories: newRootCategories })
  }

  //id: The id of the selected category
  //exclusive: if the other categories should be de-selected
  handleOnCategorySelected = (id, exclusive = true) => {
    let categories = this.state.categories

    for (let category of categories) {
      if (category.id === id) {
        category.selected = !category.selected
      } else {
        if (exclusive) {
          category.selected = false
        }
      }
    }

    storejs.set(FILTERING_CATEGORIES, categories)

    this.setState({ categories: categories })
  }

  handleVenueClick = (event, venue) => {
    const { slug, name, street_name, city } = venue
    const { showVenueURL } = this.props
    const deliveryZone = this.state.deliveryZone
    const citySlug = deliveryZone ? deliveryZone.slug : city

    const url = getVenueURL(showVenueURL, citySlug, slug)

    logVenueClicked(`${name} - ${street_name}, ${city}`)
    logVisitedVenueToIntercom(venue.id, venue.name, venue.slug)

    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      window.open(url, "_blank")
    } else {
      BrowserLocationHelper.ChangeURL(url)
    }
  }

  handleServiceClick = (venue, service) => {
    const { slug, name, street_name, city } = venue
    const { showVenueURL } = this.props
    const deliveryZone = this.state.deliveryZone
    const citySlug = deliveryZone ? deliveryZone.slug : city

    const url = getVenueURL(showVenueURL, citySlug, slug)

    logServiceClicked(
      `${service?.name || "no_name"} - ${name} - ${street_name}, ${city}`
    )

    BrowserLocationHelper.ChangeURL(url + `?service_id=${service.id}`)
  }

  handleShowFilterModal = () => {
    this.setState({ showFilterModal: true })
  }

  handleOnCloseFilterModal = () => {
    this.setState({ showFilterModal: false })
  }

  handleOnClearFilterClicked = () => {
    storejs.set(FILTERING_CATEGORIES, null)
    storejs.set(FILTERING_FEATURES, null)

    this.setState({
      showFilterModal: false,
      selectedFeatures: getInitialSelectedFeatures(),
      categories: getInitialCategories(this.state.categories),
    })
  }

  handleTakeawayChange = () => {
    this.setState({ takeAwayChangeLoading: true }, () => {
      const queryParam = !this.state.takeAwaySelected ? { take_away: true } : {}

      VenuesAPI.getVenues(this.props.location, queryParam)
        .then((data) => {
          this.setState({
            takeAwayChangeLoading: false,
            loaded: true,
            zoneNotCovered: false,
            venues: data.venues,
            categories: getInitialCategories(data.venue_categories),
            rootCategories: getRootCategories(data.venue_categories)
          })
          this.toggleTakeAwayModeWithLocalStorage(!this.state.takeAwaySelected)
        })
        .catch((err) => {
          // TODO: handle all error cases (i.e. err is undefined)
          if (err && err.indexOf && err.indexOf("zone-not-covered") !== -1) {
            this.setState({
              takeAwayChangeLoading: false,
              loaded: true,
              venues: [],
              categories: [],
              rootCategories: [],
              zoneNotCovered: true,
            })
          } else {
            this.setState({ takeAwayChangeLoading: false })
          }
        })
    })
  }

  handleOnFeatureSelected = (name) => {
    const selectedFeatures = this.state.selectedFeatures
    selectedFeatures[name] = !selectedFeatures[name]

    storejs.set(FILTERING_FEATURES, selectedFeatures)
    this.setState({ selectedFeatures })
  }

  handleSearchOnVenues = (text) => {
    // filter venues and venues' services based on searchbar text
    if (!!text.trim()) {
      let filtered = this.state.venues.filter((el) => {
        return (el?.name || "").toLowerCase().includes(text.toLowerCase())
      })
      this.setState({ isSearching: true, searchFilteredVenues: filtered })
      this.handleSearchOnServices(text)
    } else {
      this.setState({
        isLoading: false,
        isSearching: false,
        searchFilteredServices: [],
      })
    }
  }

  handleSearchOnServices = (text) => {
    let venueIds = this.state.venues.map((v) => v.id)
    this.setState({ isLoading: true })
    GetSearchedServices(text, venueIds)
      .then((res) => {
        this.setState({
          isLoading: false,
          searchFilteredServices: res?.dishes || [],
        })
      })
      .catch((err) => {
        this.setState({ isLoading: false })
      })
  }

  getSelectedVenueCategories() {
    return this.state.categories.filter(
      (category) => category.selected === true
    )
  }

  getAppliedFiltersCount() {
    let count = this.getSelectedVenueCategories().length

    count += Object.values(this.state.selectedFeatures).filter(
      (value) => value === true
    ).length

    return count
  }

  getOrderMode() {
    return this.state.takeAwaySelected ? ORDER_MODE.PICKUP : ORDER_MODE.DELIVERY
  }

  renderTakeAwayAndSearchBar() {
    const handler = debounce(this.handleSearchOnVenues, 1000)

    return (
      <Fragment>
        <div className="row pr-2">
          <Col xs={10}>
            {I18n.t("pages.locations.venues.takeaway_description")}
          </Col>
          <Switch
            disabled={this.state.takeAwayChangeLoading || false}
            checked={this.state.takeAwaySelected}
            onChange={this.handleTakeawayChange}
            onColor="#00D174"
            onHandleColor="#fff"
            handleDiameter={20}
            uncheckedIcon={false}
            checkedIcon={false}
            height={24}
            width={48}
            className="react-switch"
            id="customer_marketing"
          />
        </div>
        <hr/>
        <VenueChooserSearchBar
          onChange={handler}
          isLoading={this.state.isLoading}
        />
      </Fragment>
    )
  }

  render() {
    if (this.state.errorMessage) {
      return <ErrorMessage message={this.state.errorMessage}/>
    }

    if (!this.state.loaded) {
      return <LoadingVenues/>
    }

    const selectedRootCategory = this.state.rootCategories.find((category) => !!category.selected)
    const venues = getVisibleVenues(
      this.state.isSearching ? this.state.searchFilteredVenues : this.state.venues,
      this.state.categories,
      this.getSelectedVenueCategories(),
      selectedRootCategory,
      this.state.selectedFeatures)

    const { zoneNotCovered } = this.state
    const { location, userToken } = this.props
    const appliedFiltersCount = this.getAppliedFiltersCount()
    const venuesCount = venues.length

    return (
      <div className="search-content">
        <div className="visible-xs visible-sm">
          {this.renderTakeAwayAndSearchBar()}
        </div>
        <div className="layout-container">
          <StickyBox
            offsetTop={101}
            className="visible-md visible-lg col-md-4 in-page-filters"
          >
            <div className={style.filteringContainer}>
              <div className={style.filterSection}>
                {I18n.t("pages.locations.venues.takeaway_label")}{" "}
                <i className="fal fa-person-carry"/>
              </div>
              {this.renderTakeAwayAndSearchBar()}
              <div className="hidden-xs hidden-sm">
                <div className={style.filterSection}>
                  {I18n.t("pages.locations.venues.features.title")}
                </div>
                <VenueFeaturesChooser
                  selectedFeatures={this.state.selectedFeatures}
                  onFeatureSelected={this.handleOnFeatureSelected}
                />
                <hr/>
                <div className={style.filterSection} style={{ marginTop: 40 }}>
                  {I18n.t("pages.locations.venues.cuisines.title")}
                </div>
                <VenueCategoriesChooser
                  categories={this.state.categories}
                  onSelect={this.handleOnCategorySelected}
                  onAllCategoriesSelected={this.handleOnAllCategoriesSelected}
                />
              </div>
            </div>
          </StickyBox>

          <div className="search-results">
            <div className="row p-4 pt-0">
              <CampaignsBannerHorizontalList
                latitude={location.latitude}
                longitude={location.longitude}
                userToken={userToken}
              />
            </div>

            <div className="row p-4 pt-0">
              <CategoriesHorizontalList categories={this.state.rootCategories}
                                        min={2}
                                        onCategoryClick={this.handleOnRootCategorySelected}/>
            </div>

            {selectedRootCategory && <div className="row p-4 pt-0">
              <SubCategoriesHorizontalList categories={this.state.categories}
                                           selectedRootCategory={selectedRootCategory}
                                           onCategoryClick={(id) => this.handleOnCategorySelected(id, true)}/>
            </div>}

            {appliedFiltersCount === 0 && <div className="row px-4 pt-0">
              <VenuesCarousels
                venues={venues}
                onVenueClick={this.handleVenueClick}
                latitude={location.latitude}
                longitude={location.longitude}
                orderMode={this.getOrderMode()}
                userToken={userToken} />
            </div>}

            {selectedRootCategory && <div className="row px-4 pt-0">
              <SelectedCategoriesTitle categories={this.state.categories}
                                       onClick={this.handleOnRootCategorySelected}
                                       selectedRootCategory={selectedRootCategory}/>
            </div>}

            <div className="row pb-4 pt-0">
              {venuesCount > 0 && (
                <div className="in-page-filters__results-count pl-4">
                  <strong>{venuesCount}</strong>{" "}
                  {venuesCount > 1
                    ? I18n.t("pages.locations.venues.plural")
                    : I18n.t("pages.locations.venues.singular")}
                </div>
              )}

              {appliedFiltersCount > 0 && (
                <div
                  onClick={this.handleOnClearFilterClicked}
                  className="in-page-filters__remove pr-4"
                >
                  <i className="fal fa-close mr-2"/>
                  {I18n.t("pages.locations.venues.features.remove_filters") +
                    " "}
                  <strong>({appliedFiltersCount})</strong>
                </div>
              )}
            </div>

            {zoneNotCovered && <ZoneNotCovered location={location}/>}
            {!zoneNotCovered &&
            venuesCount === 0 &&
            !this.state.isLoading &&
            this.state.searchFilteredServices.length === 0 ? (
              <EmptyVenuesAlert/>
            ) : (
              <div className={style.resultContainer}>
                <VenuesList
                  orderMode={this.getOrderMode()}
                  venues={venues}
                  onClick={this.handleVenueClick}
                />
                <ServicesList
                  orderMode={this.getOrderMode()}
                  isLoading={this.state.isLoading}
                  venues={this.state.venues}
                  services={this.state.searchFilteredServices}
                  onClick={this.handleServiceClick}
                />
              </div>
            )}
          </div>
        </div>

        <div className="hidden-lg hidden-md">
          <div
            className="btn btn-orange btn-lg venues-filters-circle-button"
            onClick={this.handleShowFilterModal}
          >
            <i className="fal fa-sliders-h mr-2"/>
            <span className="value">{appliedFiltersCount}</span>
          </div>

          <VenuesFilterModal
            show={this.state.showFilterModal}
            categories={this.state.categories}
            onCategorySelected={this.handleOnCategorySelected}
            onAllCategoriesSelected={this.handleOnAllCategoriesSelected}
            onClose={this.handleOnCloseFilterModal}
            selectedFeatures={this.state.selectedFeatures}
            onFeatureSelected={this.handleOnFeatureSelected}
            onClearFilterClicked={this.handleOnClearFilterClicked}
            resultsCount={venues.length}
          />
        </div>
      </div>
    )
  }
}

VenueChooserApp.propTypes = {
  showVenueURL: PropTypes.string.isRequired,
}
